import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL, AUTH_BASE_URL, LOGIN_PROVIDER, LOGIN_PROVIDER_NAME } from "../config";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import useInstanceInfo from "../hooks/useInstanceInfo";
import LegalLink from "./shared/LegalLink";

// Check if user only has restricted SELF-scoped roles for this org (from the validate result).
// RISK_MANAGER+ at SELF scope gets full org view; only CONTRIBUTOR/APPROVER/VIEWER are restricted
const RESTRICTED_SELF_ROLES = ['CONTRIBUTOR', 'APPROVER', 'VIEWER'];
const isRestrictedSelfScope = (roles, orgId, dealHierarchy, businessUnitHierarchy) => {
    if (!roles?.length || !orgId) return false;
    const parsedOrgId = parseInt(orgId);
    // If user has any role at ACCOUNT/TENANT/ORG scope, they have full access
    const hasOrgPlusScope = roles.some(r => ['ACCOUNT', 'TENANT', 'ORG'].includes(r.scope));
    if (hasOrgPlusScope) return false;

    // DEAL-scoped role for a deal in this org = NOT restricted
    if (dealHierarchy?.some(d => d.orgId === parsedOrgId) &&
        roles.some(r => r.scope === 'DEAL'))
      return false;

    // BU-scoped role for a BU in this org = NOT restricted
    if (businessUnitHierarchy?.some(b => b.orgId === parsedOrgId) &&
        roles.some(r => r.scope === 'BUSINESS_UNIT'))
      return false;

    // Check if user has a SELF-scoped role for this org
    const selfRoles = roles.filter(r => r.scope === 'SELF' && r.scopeEntityId === parsedOrgId);
    if (selfRoles.length === 0) return false;
    // RISK_MANAGER+ at SELF scope → NOT restricted (gets full org view)
    return selfRoles.every(r => RESTRICTED_SELF_ROLES.includes(r.role));
};

const Login = () => {
    const navigate = useNavigate();
    const { user, userRoles, dealHierarchy, businessUnitHierarchy, initialized, authError } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const routedRef = useRef(false);
    // Installation settings: contact address, legal links and the marketing link. All null (so
    // nothing renders) until the server answers, and on failure.
    const { info: instanceInfo } = useInstanceInfo();
    // "Create an account" is hidden unless the backend's Signup:ShowSignupLink is on. Anonymous
    // endpoint, so plain axios (like the signup pages) rather than the api instance, whose
    // org headers and 401/403 redirects don't belong on the sign-in page. On any error the
    // link stays hidden. /signup itself still works by URL.
    const [showSignupLink, setShowSignupLink] = useState(false);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_BASE_URL}/Signup/link`)
            .then((res) => { if (!cancelled) setShowSignupLink(res.data?.showSignupLink === true); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // If a cookie session already exists (bootstrapped by AuthContext), route to the right landing page.
    useEffect(() => {
        if (!initialized || !user || routedRef.current) return;
        routedRef.current = true;
        routeAfterLogin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized, user]);

    const routeAfterLogin = async () => {
        setLoading(true);
        const result = user; // /Auth/validate payload
        const savedReturn = sessionStorage.getItem("returnUrl");
        const isValidReturnUrl = (url) => url && url.startsWith('/') && !url.includes('://') && !url.startsWith('//');
        const restricted = isRestrictedSelfScope(userRoles, result.organizationId, dealHierarchy, businessUnitHierarchy);

        if (result?.organizationId) {
            // Store selected organization to localStorage only when we have a valid org
            const matchedOrg = result.organizations?.find(o => o.organizationId === result.organizationId);
            localStorage.setItem(
                "selectedOrganization",
                JSON.stringify(matchedOrg || {
                    tenantId: result.tenantId || 0,
                    organizationId: result.organizationId,
                    organizationName: "",
                })
            );
            sessionStorage.removeItem("returnUrl");
            navigate(isValidReturnUrl(savedReturn) ? savedReturn : (restricted ? '/my-tasks' : '/dashboard'));
        } else if (result?.tenantId > 0) {
            // Check if user has only one organization - if so, auto-select it
            try {
                const orgsResponse = await api.get(`/Organizations`);
                const organizations = orgsResponse.data;
                if (organizations.length === 1) {
                    const org = organizations[0];
                    localStorage.setItem("selectedOrganization", JSON.stringify(org));
                    const singleOrgRestricted = isRestrictedSelfScope(userRoles, org.organizationId, dealHierarchy, businessUnitHierarchy);
                    sessionStorage.removeItem("returnUrl");
                    navigate(isValidReturnUrl(savedReturn) ? savedReturn : (singleOrgRestricted ? '/my-tasks' : '/dashboard'));
                } else {
                    // Multiple orgs or no orgs - go to tenant selection (leave returnUrl for it to consume)
                    navigate("/organizations");
                }
            } catch {
                navigate("/organizations");
            }
        } else if (result?.isSuperAdmin) {
            // A SuperAdmin with no organization (e.g. the bootstrap admin on a fresh install) has
            // nothing to select yet: land on the SuperAdmin page to approve the first signup.
            sessionStorage.removeItem("returnUrl");
            navigate("/super-admin");
        } else {
            // No organizationId and no tenantId
            setError("Unable to determine your organization. Please contact your administrator.");
            setLoading(false);
            routedRef.current = false;
        }
    };

    const handleLogin = () => {
        setLoading(true);
        setError("");
        // Full-page redirect to the backend OIDC sign-in; it returns to the SPA /login after auth,
        // where the cookie session is bootstrapped and routeAfterLogin sends the user onward.
        const returnUrl = `${window.location.origin}/login`;
        window.location.href = `${AUTH_BASE_URL}/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    };

    // Customer-hosted installs with a non-Microsoft identity provider show provider-neutral copy.
    const isOidc = LOGIN_PROVIDER === "oidc";
    const providerName = LOGIN_PROVIDER_NAME || "Single Sign-On";

    // Footer links the installation configures; an unset document or address is not shown.
    const footerLinkClass = "hover:text-gray-600 transition-colors";
    const footerLinks = [
        instanceInfo.legal.privacyPolicyUrl && {
            key: "privacy",
            node: <LegalLink url={instanceInfo.legal.privacyPolicyUrl} className={footerLinkClass}>Privacy Policy</LegalLink>,
        },
        instanceInfo.legal.termsOfUseUrl && {
            key: "terms",
            node: <LegalLink url={instanceInfo.legal.termsOfUseUrl} className={footerLinkClass}>Terms of Use</LegalLink>,
        },
        instanceInfo.contactEmail && {
            key: "contact",
            node: <a href={`mailto:${instanceInfo.contactEmail}`} className={footerLinkClass}>Contact</a>,
        },
    ].filter(Boolean);

    // The identity signed in, but no active Axaris account owns it (AuthContext keeps the
    // /Auth/validate error). Signing in again would land here again, so offer Sign out as well.
    const INVITE_HINT = "Open the invitation link you were sent, or ask your administrator to invite you.";
    let accountNotice = null;
    if (authError?.code === "user_not_found") {
        accountNotice = authError.email
            ? `You're signed in as ${authError.email}, but there's no Axaris account for it yet. ${INVITE_HINT}`
            : `You're signed in, but there's no Axaris account for this sign-in yet. ${INVITE_HINT}`;
    } else if (authError?.code === "user_inactive") {
        accountNotice = authError.message || "Your account has been deactivated. Contact your administrator.";
    }

    // Spinner while bootstrapping the session, routing an authenticated user, or redirecting to sign in.
    const showSpinner = !error && (loading || !initialized || !!user);

    if (showSpinner) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
                    style={{ background: "radial-gradient(circle, #bfdbfe, #e0f2fe, transparent)" }} />
                <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
                    style={{ background: "radial-gradient(circle, #ddd6fe, #fbcfe8, transparent)" }} />
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                    style={{ backgroundImage: "radial-gradient(circle, #94a3b8 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

                <div className="relative z-10 logo-wordmark logo-wordmark-lg items-center mb-8">
                    <span className="logo-wordmark-text logo-wordmark-text-gradient">AXARIS™</span>
                    <span className="logo-wordmark-sub text-[#A0A3BA]">By Cloud Guidant</span>
                </div>

                <div className="relative z-10 w-full max-w-[520px] bg-white rounded-3xl shadow-2xl shadow-gray-200/60 border border-gray-100 p-12 flex flex-col items-center justify-center gap-4">
                    <div className="h-12 w-12 animate-spin rounded-full border-2 border-transparent border-t-cyan-500 border-r-purple-500" />
                    <p className="text-sm text-gray-500 font-medium">Signing you in securely…</p>
                    <p className="text-xs text-gray-400">{isOidc ? `Redirecting to ${providerName}` : "Redirecting to Microsoft authentication"}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">

            {/* Background orbs */}
            <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
                style={{ background: "radial-gradient(circle, #bfdbfe, #e0f2fe, transparent)" }} />
            <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
                style={{ background: "radial-gradient(circle, #ddd6fe, #fbcfe8, transparent)" }} />
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: "radial-gradient(circle, #94a3b8 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

            {/* Logo — top center */}
            <div className="relative z-10 logo-wordmark logo-wordmark-lg items-center mb-8">
                <span className="logo-wordmark-text logo-wordmark-text-gradient">AXARIS™</span>
                <span className="logo-wordmark-sub text-[#A0A3BA]">By Cloud Guidant</span>
            </div>

            {/* Card */}
            <div className="relative z-10 w-full max-w-[520px] bg-white rounded-3xl shadow-2xl shadow-gray-200/60 border border-gray-100 p-12">

                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome back</h1>
                    <p className="text-sm text-gray-500 leading-relaxed">
                        {isOidc ? "Sign in securely using your organization account" : "Sign in securely using your Work or School Microsoft account"}
                    </p>
                </div>

                {/* Error */}
                {error && (
                    <div className="mb-6 flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                        <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                {/* No Axaris account for this sign-in, or a deactivated one */}
                {accountNotice && (
                    <div role="alert" className="mb-6 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm text-amber-800">{accountNotice}</p>
                        <Link to="/logout"
                            className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-xl border border-amber-300 bg-white text-sm font-semibold text-amber-800 hover:bg-amber-100 transition-colors">
                            Sign out
                        </Link>
                    </div>
                )}

                {/* Sign-in button: Microsoft logo (SaaS) or a generic key icon (OIDC) */}
                <button
                    onClick={handleLogin}
                    className="w-full flex items-center gap-3 px-5 py-4 bg-white border-2 border-gray-200 rounded-2xl hover:border-blue-400 hover:shadow-lg hover:shadow-blue-100/60 transition-all duration-200 font-semibold text-gray-800 text-sm group"
                >
                    {isOidc ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                    ) : (
                        <svg width="22" height="22" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                        </svg>
                    )}
                    <span className="flex-1">{isOidc ? `Sign in with ${providerName}` : "Sign in with Microsoft"}</span>
                    <svg className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>

                {/* Trust block */}
                <div className="mt-4 px-4 py-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
                    <p className="flex items-center gap-2 text-xs text-gray-500">
                        <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        {isOidc
                            ? `Authentication is handled by ${providerName}. We never see your password.`
                            : "Authentication is handled by Microsoft. We never see your password."}
                    </p>
                    <p className="flex items-center gap-2 text-xs text-gray-400">
                        <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        {isOidc
                            ? `You will be redirected to ${providerName} to complete sign-in`
                            : "You will be redirected to Microsoft to complete sign-in"}
                    </p>
                </div>

                {showSignupLink && (
                    <>
                        {/* Divider */}
                        <div className="my-6 flex items-center gap-3">
                            <div className="flex-1 h-px bg-gray-100" />
                            <span className="text-xs text-gray-400 font-medium">New to AXARIS?</span>
                            <div className="flex-1 h-px bg-gray-100" />
                        </div>

                        {/* Sign up */}
                        <Link to="/signup"
                            className="w-full flex items-center justify-center px-5 py-3.5 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150">
                            Create an account
                        </Link>
                    </>
                )}
            </div>

            {/* Back to Home (marketing site; off on customer-hosted installs) */}
            {instanceInfo.showMarketingSiteLink && (
                <a href="/landing.html" className="relative z-10 mt-5 inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors group">
                    <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Home
                </a>
            )}

            {/* Footer: only the links this installation configures, separated by dots */}
            {footerLinks.length > 0 && (
                <p className="relative z-10 mt-4 text-center text-xs text-gray-400">
                    {footerLinks.map((link, i) => (
                        <React.Fragment key={link.key}>
                            {i > 0 && <span className="mx-2">·</span>}
                            {link.node}
                        </React.Fragment>
                    ))}
                </p>
            )}
        </div>
    );
};

export default Login;
